import React, { MouseEventHandler, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  Button,
  Icon,
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from '@socialchorus/shared-ui-components';

import { trackContentCardComment } from '../../../models/content/analytics';
import { contentSelectors, contentOperations } from '../../../models/content';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { ForwardActions } from '../../../config/history';
import { fetchCommentSummary } from '../../../services/comment';

interface IContentActionComment {
  showText?: boolean;
  commentCount: number;
  onClick: MouseEventHandler | undefined;
  contentTitle: string;
  container?: HTMLElement | null;
  loadingComments: boolean;
}

// Pure Component
const ContentActionComment: React.FC<IContentActionComment> = ({
  showText = false,
  commentCount = 0,
  onClick,
  contentTitle,
  container,
  loadingComments,
}) => {
  const { t } = useTranslation(undefined, { useSuspense: false });

  const actionLabel = t('content.comment_action', { title: contentTitle });
  const label = loadingComments
    ? t('content.loading_comments')
    : `${t('content.comment')} (${commentCount})`;
  const noShowTextLabel = loadingComments ? '' : `${commentCount}`;
  showText = false;

  return showText ? (
    <Button
      variant="text"
      onClick={onClick}
      aria-label={actionLabel}
      label={label}
      leftIcon={<Icon>chat_bubble</Icon>}
    ></Button>
  ) : (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <Button
          variant="text"
          onClick={onClick}
          aria-label={actionLabel}
          label={noShowTextLabel}
          leftIcon={<Icon>chat_bubble</Icon>}
        ></Button>
      </TooltipTrigger>
      <TooltipContent
        description={t('content.comment')}
        container={container}
      />
    </TooltipRoot>
  );
};

type ContentActionCommentWrapperProps = {
  contentId: number;
  analyticsData: IAnalyticsData;
  showText?: boolean;
  onClickAction?: ForwardActions;
  container?: HTMLElement | null;
};

// Data Wrapper
const ContentActionCommentWrapper = ({
  contentId,
  analyticsData,
  showText,
  onClickAction = 'push',
  container,
}: ContentActionCommentWrapperProps) => {
  const [isLoadingCommentCount, setIsLoadingCommentCount] =
    useState<boolean>(true);

  const dispatch = useDispatch();

  // Fetch comment count via API and update Redux
  useEffect(() => {
    const loadCommentCount = async () => {
      try {
        setIsLoadingCommentCount(true);
        const response = await fetchCommentSummary(contentId);
        const commentCount =
          response?.data?.data?.attributes?.commentCount ?? 0;

        // Update Redux store with the fetched count
        dispatch(contentOperations.setCommentCount(contentId, commentCount));
      } catch (error) {
        console.error('Failed to fetch comment count:', error);
        dispatch(contentOperations.setCommentCount(contentId, 0));
      } finally {
        setIsLoadingCommentCount(false);
      }
    };

    loadCommentCount();
  }, [contentId, dispatch]);

  const contentTitle = usePatronSelector((s) =>
    contentSelectors.getContentFunctionalTitle(s, { contentId })
  );
  const commentCount = usePatronSelector((state) =>
    contentSelectors.getContentCommentCount(state, { contentId })
  );
  const history = useHistory();

  const handleClick = () => {
    const commentsHash = '#comments';
    const targetPath = `/contents/${contentId}`;

    history[onClickAction]({
      pathname: targetPath,
      hash: commentsHash,
    });

    trackContentCardComment(contentId, analyticsData);
  };

  return (
    <ContentActionComment
      commentCount={commentCount}
      onClick={handleClick}
      contentTitle={contentTitle}
      showText={showText}
      container={container}
      loadingComments={isLoadingCommentCount}
    />
  );
};

export { ContentActionComment };

export default ContentActionCommentWrapper;
